import { useCallback } from "react";

import { InfoItem } from "models/checklist/checklist-item/checklist-item";
import AnalyticsService from "services/analytics/analytics";
import Pax from "models/pax";
import PaxSession, { PaxSessionId } from "models/session/pax-session";
import ChecklistItemStatus from "models/checklist/checklist-item/checklist-item-status";
import { useAppStore } from "store/store";
import usePaxSession from "../usePaxSession";
import { getGeneralAnalyticsParams } from "./useGeneralAnalytics";
import useMutatedPagePath from "./useMutatedPagePath";

const getPaxType = (pax?: Pax): string => {
  const today = new Date();

  if (!pax?.birthdate) {
    return "unknown";
  }

  let age = today.getFullYear() - pax.birthdate.getFullYear();
  const m = today.getMonth() - pax.birthdate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < pax.birthdate.getDate())) {
    age--;
  }

  if (age < 2) {
    return "infant";
  }

  if (age < 18) {
    return "child";
  }

  return "adult";
};

const getQuestionsAnswered = (
  paxSession: PaxSession
): "none" | "all" | "some" => {
  const answeredQuestions = paxSession.checklist.questions.filter(
    (q) => !(q.answer === null || q.answer === undefined)
  );
  if (answeredQuestions.length === 0) {
    return "none";
  }

  if (answeredQuestions.length === paxSession.checklist.questions.length) {
    return "all";
  }

  return "some";
};

export const getPaxLevelParams = (
  paxSessions: PaxSession[],
  selectedPaxSessionId: PaxSessionId | null
): { [key: string]: any } => {
  if (!selectedPaxSessionId) {
    return {};
  }

  const paxSession = paxSessions.find(
    (p) => p.paxSessionId === selectedPaxSessionId
  );

  const options: { [key: string]: any } = {
    pax_type: getPaxType(paxSession?.pax),
    pax_status: paxSession?.checklist.resolutionStatus,
    pax_consent_state: paxSession?.consented ? 1 : 0,
  };

  const questionsAnswered = getQuestionsAnswered(paxSession!);
  options["pax_questions_answered"] = questionsAnswered;

  if (questionsAnswered === "all") {
    options["unresolved_items_number"] = paxSession?.checklist.items.filter(
      (i) => i.status === ChecklistItemStatus.NotYetResolved
    ).length;
    options["has_issues_items_number"] = paxSession?.checklist.items.filter(
      (i) => i.status === ChecklistItemStatus.VerificationRequired
    ).length;
    options["resolved_items_number"] = paxSession?.checklist.items.filter(
      (i) => i.status === ChecklistItemStatus.Resolved
    ).length;
    options["info_items_number"] = paxSession?.checklist.items.filter(
      (i) => i instanceof InfoItem
    ).length;
  }

  return options;
};

const usePaxLevelAnalytics = () => {
  const [path, title, location] = useMutatedPagePath();
  const locale = useAppStore((state) => state.locale);
  const paxSessions = useAppStore((state) => state.paxSessions);
  const paxSession = usePaxSession();
  const journey = useAppStore((state) => state.journey);

  const sendPageView = useCallback(() => {
    AnalyticsService.shared.trackEvent(
      "page_view",
      {
        ...getGeneralAnalyticsParams(
          paxSessions,
          journey,
          path,
          title,
          location,
          locale
        ),
        ...getPaxLevelParams(paxSessions, paxSession?.paxSessionId || null),
      },
      false // isScanRelated
    );
  }, [
    paxSessions,
    journey,
    path,
    title,
    location,
    locale,
    paxSession?.paxSessionId,
  ]);

  return sendPageView;
};

export default usePaxLevelAnalytics;
